import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, BookOpen, Shield, AlertTriangle, CheckCircle2, Clock,
  Loader2, UserPlus, BarChart3, Mail
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { usePermissions } from "../../context/PermissionsContext";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const statusStyles = {
  completed: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Completed" },
  in_progress: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", label: "In Progress" },
  enrolled: { color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200", label: "Enrolled" },
  overdue: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Overdue" },
  pending: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Pending" },
};

const ManagerTeamMemberPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/manager/team/${userId}/details`).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <AdminLayout title="Team Member" variant="manager">
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!data) return (
    <AdminLayout title="Team Member" variant="manager">
      <div className="text-center py-24 text-muted-foreground">Member not found</div>
    </AdminLayout>
  );

  const { member, courses, compliance, stats } = data;
  const st = (s) => statusStyles[s] || statusStyles.pending;

  return (
    <AdminLayout title={member.full_name} variant="manager">
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="team-member-page">
        {/* Back + Header */}
        <motion.div variants={item}>
          <Button variant="ghost" size="sm" className="gap-1.5 mb-3 -ml-2" onClick={() => navigate("/manager/team")} data-testid="back-to-team">
            <ArrowLeft className="w-4 h-4" /> Back to Team
          </Button>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 border-2 border-primary/20">
              <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">{getInitials(member.full_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-2xl font-extrabold tracking-tight">{member.full_name}</h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-sm text-muted-foreground">{member.job_title || "Team Member"}</span>
                {member.department && <Badge variant="outline" className="text-xs">{member.department}</Badge>}
                <Badge variant={member.status === "active" ? "default" : "secondary"} className="text-xs">{member.status}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Mail className="w-3 h-3" /> {member.email}</p>
            </div>
            {hasPermission("enrollments.create") && (
              <Button className="gap-2" onClick={() => navigate(`/manager/team-enroll?member=${userId}`)} data-testid="enroll-member-btn">
                <UserPlus className="w-4 h-4" /> Enroll in Course
              </Button>
            )}
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div variants={item} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Courses", value: stats.total_courses, icon: BookOpen, color: "text-blue-600 bg-blue-50" },
            { label: "Completed", value: stats.completed, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
            { label: "Completion Rate", value: `${stats.completion_rate}%`, icon: BarChart3, color: "text-violet-600 bg-violet-50" },
            { label: "Compliance Overdue", value: stats.compliance_overdue, icon: AlertTriangle, color: stats.compliance_overdue > 0 ? "text-red-600 bg-red-50" : "text-emerald-600 bg-emerald-50" },
          ].map((s, i) => (
            <Card key={i} className="border">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", s.color)}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="text-xl font-bold">{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Tabs */}
        <motion.div variants={item}>
          <Tabs defaultValue="courses" className="space-y-4">
            <TabsList data-testid="member-tabs">
              <TabsTrigger value="courses" className="gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Courses ({courses.length})</TabsTrigger>
              <TabsTrigger value="compliance" className="gap-1.5"><Shield className="w-3.5 h-3.5" /> Compliance ({compliance.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="courses" className="space-y-2" data-testid="member-courses-tab">
              {courses.length === 0 ? (
                <Card className="border"><CardContent className="p-6 text-center text-muted-foreground">No courses assigned yet</CardContent></Card>
              ) : courses.map((c, i) => {
                const s = st(c.status);
                return (
                  <Card key={i} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{c.course_title}</p>
                          <p className="text-xs text-muted-foreground">{c.category || "Uncategorized"}</p>
                        </div>
                        <div className="hidden sm:block w-32">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-medium">{c.progress_percent}%</span>
                          </div>
                          <Progress value={c.progress_percent} className="h-1.5" />
                        </div>
                        {c.score != null && <div className="text-center"><p className="text-sm font-bold">{c.score}%</p><p className="text-[10px] text-muted-foreground">Score</p></div>}
                        <Badge className={cn("text-xs", s.bg, s.color, s.border)} variant="outline">{s.label}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            <TabsContent value="compliance" className="space-y-2" data-testid="member-compliance-tab">
              {compliance.length === 0 ? (
                <Card className="border"><CardContent className="p-6 text-center text-muted-foreground">No compliance assignments</CardContent></Card>
              ) : compliance.map((c, i) => {
                const s = st(c.status);
                return (
                  <Card key={i} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", s.bg)}>
                          <s.icon className={cn("w-4 h-4", s.color)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm truncate">{c.title}</p>
                            {c.is_mandatory && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Mandatory</Badge>}
                          </div>
                          {c.due_date && <p className="text-xs text-muted-foreground">Due: {c.due_date}</p>}
                        </div>
                        <Badge className={cn("text-xs", s.bg, s.color, s.border)} variant="outline">{s.label}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>
          </Tabs>
        </motion.div>
      </motion.div>
    </AdminLayout>
  );
};

export default ManagerTeamMemberPage;
